import React, { useEffect, useRef, useState } from 'react'
import { comDestaque } from '../../lib/destaque'
import { IconeAlvo, IconeConversa, IconeCalculadora, IconeBarras, IconeRaio, IconeBanco } from '../../lib/icones'
import { EVENTO, SOBRE_TEXTO, NUMEROS_APRESENTA, PLATAFORMA, ACOMPANHAMENTO } from '../../dados/evento'

const ICONES = {
  alvo: IconeAlvo,
  conversa: IconeConversa,
  calculadora: IconeCalculadora,
  barras: IconeBarras,
  raio: IconeRaio,
  banco: IconeBanco,
}

/**
 * 7ª dobra — sobre a Apresenta.me.
 *
 * ⚠️ VOLTOU A SER DUAS COLUNAS em 11/08, revertendo a decisão de 10/08 (foto
 * full-bleed atrás do texto). O pedido do cliente foi literal: "minha visão é
 * que uma foto ao lado ficaria mais bacana para que não fique tão apagado com
 * a máscara, assim como está na página do CUPOLA".
 *
 * Ele tem razão pelo mesmo motivo que a foto do hero saiu do fundo: para o
 * texto passar na WCAG sobre uma foto, o véu precisa ser tão denso que a foto
 * deixa de ser vista — paga-se a banda da imagem e entrega-se um retângulo
 * escuro. Numa moldura ao lado, a mesma foto aparece com contraste pleno e o
 * texto fica sobre fundo sólido.
 *
 * O corte da foto (uma cena ampla de grupo) era o argumento contra a moldura em
 * 10/08. Resolvido com `object-position: center 35%` e proporção 4:5: o recorte
 * pega a faixa dos rostos, não o teto do salão.
 *
 * Os três números usam a classe .numeros portada do site institucional. Lá a
 * decisão foi registrada assim: em faixa eles viram prova, em parágrafo eram só
 * mais uma frase. Vale igual aqui — é o bloco que responde "quem está me
 * convidando" logo antes do rodapé.
 *
 * O CTA é o único link da página que sai do domínio — por isso o ícone de
 * "abre em outra aba" e o `target="_blank"`: em qualquer outro CTA isso
 * tiraria alguém do fluxo de inscrição sem aviso; aqui é a Apresenta.me
 * institucional, não a inscrição, então sair da aba é esperado e sinalizado.
 * Fica no fim da página de propósito: antes do formulário, mandaria embora
 * quem ainda não se inscreveu.
 */
export default function SecaoSobre({
  chamada = 'Evento realizado pela Apresenta.me',
  texto = SOBRE_TEXTO,
  /* Trecho em azul dentro do parágrafo. Precisa ser um pedaço EXATO do texto
     acima; não batendo, a frase sai inteira sem destaque, sem quebrar a dobra
     (ver comDestaque em lib/destaque.jsx). Mudou junto com o texto novo de
     11/08 — antes era "tudo em uma só plataforma", que a reescrita do cliente
     trocou por "em um só lugar". */
  textoDestaque = 'em um só lugar',
  numeros = NUMEROS_APRESENTA,
  plataforma = PLATAFORMA,
  acompanhamento = ACOMPANHAMENTO,
  /* FOTO NOVA DA EQUIPE (12/08, PDF do cliente): "mudar a imagem dos
     colaboradores para a imagem abaixo", com link do Drive no fim do
     documento. Baixada, recortada na faixa dos rostos (o original tem
     6000×11166, com muito céu em cima e muito capim embaixo) e reduzida a
     1800px. A anterior era um recorte de bastidor de evento; esta é a foto
     oficial do time. */
  imagem = '/img/equipe-rota-web.jpg',
  textoCta = 'Conheça a Apresenta.me',
  urlCta = EVENTO.siteInstitucional,
  /* Nauta flutuando com o tile do logo — a pose "tecnológica" do kit,
     reservada a blocos sobre a plataforma (ver o comentário completo junto
     de `.sobre-mascote` em componentes.css). Some abaixo de 64rem, no mesmo
     corte do mascote do hero: em coluna única ele cairia sobre o texto. */
  mascote = '/img/nauta-rota-5-web.png',
}) {
  const [mascoteFalhou, setMascoteFalhou] = useState(false)
  const mascoteRef = useRef(null)

  useEffect(() => {
    const img = mascoteRef.current
    if (img && img.complete && img.naturalWidth === 0) setMascoteFalhou(true)
  }, [mascote])

  return (
    /* CHÃO CLARO (12/08, PDF do cliente): "destacar o bloco colocando fundo
       branco". É a quarta e última pausa clara da página, e a que fecha o
       argumento institucional antes das cotas e do rodapé — os dois voltam ao
       navy. Ver o ritmo inteiro no cabeçalho de revisao.css. */
    <section id="sobre" className="secao secao-sobre dia dobra-clara">
      <div className="container-site sobre-interno">
        <div className="sobre-texto">
          <span className="eyebrow">Quem realiza</span>
          <h2>{chamada}</h2>
          <p className="sobre-descricao">{comDestaque(texto, textoDestaque)}</p>

          {/* Os números voltaram para a coluna de texto (13/08, 2ª rodada).
              Passaram uma rodada na coluna visual, empilhados com
              `space-between`, e o resultado foi um zigue-zague: três blocos de
              ~110px espalhados por 458px de altura, com vãos de ~170px entre
              eles. Aqui eles são uma faixa de três, com o mesmo topo e o mesmo
              rodapé — a leitura é horizontal, que é como número comparável se
              lê. */}
          <dl className="numeros">
            {numeros.map((n) => (
              <div key={n.rotulo}>
                <dt>{n.valor}</dt>
                <dd>{n.rotulo}</dd>
              </div>
            ))}
          </dl>

          <a
            className="btn btn-rota sobre-cta"
            href={urlCta}
            target="_blank"
            rel="noopener noreferrer"
          >
            {textoCta}
            {/* Ícone de "abre em outra aba" — Tabler Icons (external-link),
                mesma fonte dos ícones da 2ª dobra. `aria-hidden`: quem usa
                leitor de tela já ouve isso pelo comportamento do link, não
                precisa de anúncio duplicado. */}
            <svg
              className="sobre-cta-icone"
              aria-hidden="true"
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M11 7H6a2 2 0 0 0 -2 2v9a2 2 0 0 0 2 2h9a2 2 0 0 0 2 -2v-5" />
              <path d="M10 14l10 -10" />
              <path d="M15 4l5 0l0 5" />
            </svg>
          </a>
        </div>

        {/* COLUNA VISUAL — o produto em primeiro plano e o time atrás dele.
            Antes eram duas figuras em dois blocos separados (a foto do time
            aqui, a tela do sistema quarenta linhas abaixo, com título e
            parágrafo próprios); as duas juntas, em camadas, contam a mesma
            coisa numa dobra em vez de duas, que é a condensação que o briefing
            pede — "não transformar esta landing de EVENTO numa homepage
            institucional".

            A tela vem NA FRENTE porque é a prova; a equipe atrás, porque é o
            contexto. Nenhum conteúdo foi descartado: o parágrafo que
            acompanhava a tela virou a entrada da lista de módulos, logo
            abaixo. */}
        <div className="sobre-figura">
          {imagem && (
            <img
              className="sobre-foto"
              src={imagem}
              alt="Time da Apresenta.me reunido durante um evento"
              width="900"
              height="1125"
              loading="lazy"
            />
          )}
          {/* O NAUTA ENCOSTADO NA QUINA DE BAIXO DA FOTO.
              A tela do sistema no notebook saiu desta dobra em 13/08 (3ª
              rodada): ela empurrava a coluna visual para ~1280px de altura e
              obrigava a foto do time a dividir atenção com um print pequeno
              demais para provar qualquer coisa. Sem ela, a dobra fecha na
              altura da foto e o mascote volta ao papel que tem no kit físico —
              apoiado na moldura, não flutuando ao lado dela.

              Decorativo (`alt=""`), e some por completo em falha de
              carregamento: `alt=""` não colapsa imagem quebrada sozinho, e a
              URL é editável no Builder. */}
          {mascote && !mascoteFalhou && (
            <img
              ref={mascoteRef}
              className="sobre-mascote"
              src={mascote}
              alt=""
              width="420"
              height="620"
              loading="lazy"
              onError={() => setMascoteFalhou(true)}
            />
          )}
          {/* Nauta apoiado na quina — o mesmo truque das peças do kit, em que o
              mascote encosta na moldura em vez de flutuar solto. Decorativo
              (`alt=""`), e some por completo em falha de carregamento pelo
              mesmo motivo do mascote do hero: `alt=""` não colapsa imagem
              quebrada, e a URL é editável no Builder. */}
          {mascote && !mascoteFalhou && (
            <img
              ref={mascoteRef}
              className="sobre-mascote"
              src={mascote}
              alt=""
              width="420"
              height="620"
              loading="lazy"
              onError={() => setMascoteFalhou(true)}
            />
          )}
        </div>
      </div>

      {/* OS SEIS MÓDULOS, agora como lista de linhas e não como grade de seis
          cartões com selo de ícone. O conteúdo é o mesmo; o peso visual caiu de
          "catálogo de produto" para "nota de rodapé qualificada", que é o lugar
          dele numa landing de evento. */}
      <div className="container-site plataforma">
        <p className="plataforma-intro">
          Um sistema só, no lugar da colcha de retalhos que a maioria das imobiliárias
          opera hoje: site, CRM, locação, financeiro e atendimento conversando entre si.
        </p>
        <ul className="plataforma-grade lista-limpa">
          {plataforma.map((modulo) => {
            const Icone = ICONES[modulo.icone] || IconeAlvo
            return (
              <li key={modulo.titulo} className="plataforma-item">
                <span className="plataforma-item-icone" aria-hidden="true">
                  <Icone />
                </span>
                <div>
                  <h4>{modulo.titulo}</h4>
                  <p>{modulo.texto}</p>
                </div>
              </li>
            )
          })}
        </ul>
      </div>

      {/* ACOMPANHAMENTO — bloco azul, o único desta dobra. O cliente pediu
          ênfase no suporte e na implantação, e o argumento é justamente o
          contrário de "software": é gente. Um campo de cor separa isso do
          catálogo de módulos acima. */}
      {acompanhamento && (
        <div className="acompanhamento">
          <div className="container-site acompanhamento-interno">
            <div className="acompanhamento-texto">
              <h3>{acompanhamento.chamada}</h3>
              <p>{acompanhamento.texto}</p>
            </div>
            <ol className="acompanhamento-etapas lista-limpa">
              {acompanhamento.etapas.map((etapa, i) => (
                <li key={etapa.titulo}>
                  <span className="acompanhamento-passo" aria-hidden="true">
                    {String(i + 1).padStart(2, '0')}
                  </span>
                  <h4>{etapa.titulo}</h4>
                  <p>{etapa.texto}</p>
                </li>
              ))}
            </ol>
          </div>
        </div>
      )}
    </section>
  )
}
